import React, { useCallback } from 'react';

import { translate } from '~l10n/client';

import { AlertMessage } from '~shared/components/AlertMessage';
import { Badge } from '~shared/components/Badge';
import { Box } from '~shared/components/Box';
import { UIButton } from '~shared/components/buttons';
import { Dialog, DialogProps } from '~shared/components/Dialog';
import { Divider } from '~shared/components/Divider';
import { FieldRow } from '~shared/components/FieldRow';
import { Inline } from '~shared/components/Inline';
import { Stack } from '~shared/components/Stack';
import Textarea from '~shared/components/Textarea/Textarea';
import ToggleSwitch from '~shared/components/ToggleSwitch/ToggleSwitch';
import Tooltip from '~shared/components/Tooltip/Tooltip';
import { OneAttemptPaywall } from '~teacher/components/OneAttemptPaywall';
import { usePaywallReducer } from '~teacher/components/PaywallDialog';
import { proCheck } from '~teacher/util/proCheck';

export interface LaunchOptions {
  oneAttempt: boolean;
  allowRepeatResponses: boolean;
  requireNames: boolean;
  questionText: string;
}

export interface Props extends Pick<DialogProps, 'open' | 'onClose'> {
  onConfirmClick: (arg0: LaunchOptions) => void;
  isRostered: boolean;
}

function OneAttemptLabel({
  canLimitOneAttempt,
}: {
  canLimitOneAttempt: boolean;
}) {
  return (
    <Inline space="2" alignY="center">
      <>{translate('One Attempt')}</>
      {canLimitOneAttempt ? null : <Badge>Pro</Badge>}
      <Tooltip
        label={translate(
          'Students can only take the quiz once. If they leave and rejoin the room, they will continue the quiz where they left off.'
        )}
      >
        <Box className="flex" tabIndex={0}>
          <svg
            className="pointer-events-none fill-cyan-600"
            width="24"
            height="24"
            viewBox="0 0 24 24"
          >
            <path
              fillRule="evenodd"
              d="M12 3a9 9 0 110 18 9 9 0 010-18zm.84 6.91h-1.67V17h1.67V9.91zm-.83-2.822c-.536 0-.97.407-.97.91 0 .498.434.905.97.905.53 0 .964-.407.964-.905 0-.503-.434-.91-.965-.91z"
            />
          </svg>
        </Box>
      </Tooltip>
    </Inline>
  );
}

export function ShortAnswerLaunchDialog({
  open,
  onClose,
  onConfirmClick,
  isRostered,
}: Props) {
  const [isConfirmButtonDisabled, setIsConfirmButtonDisabled] =
    React.useState(false);
  const [formData, setFormData] = React.useState({
    requireNames: isRostered,
    oneAttempt: isRostered,
    allowRepeatResponses: false,
    questionText: '',
  } as LaunchOptions);
  const hasPaidPlan = proCheck.hasPaidPlan();
  const canLimitOneAttempt = proCheck.canLimitOneAttempt();

  const [showRosterMessage, setShowRosterMessage] = React.useState(false);
  const [{ open: paywallOpen }, dispatch] = usePaywallReducer();

  const handleOneAttemptChange = useCallback(
    (checked) => {
      if (!isRostered && hasPaidPlan) {
        setShowRosterMessage(true);
      } else {
        canLimitOneAttempt
          ? setFormData({ ...formData, oneAttempt: checked })
          : dispatch({ type: 'OPEN' });
      }
    },
    [isRostered, hasPaidPlan, canLimitOneAttempt, formData, dispatch]
  );

  return (
    <>
      <Dialog
        id="short-answer-launch"
        open={open}
        title={translate('Short Answer')}
        onClose={onClose}
        closeLabel={translate('Close')}
      >
        <div className="pt-8">
          <Divider />
        </div>
        <Stack dividers>
          <Stack>
            <label
              className="inline-block pt-5 text-base text-blue-900"
              id="question-text-label"
              htmlFor="question-text"
            >
              {translate('Question (optional)')}
            </label>
            <Textarea
              className="w-full mt-3 mb-5"
              id="question-text"
              placeholder={translate('How‘d we do?')}
              value={formData.questionText}
              minRows={2}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  questionText: e.target.value,
                })
              }
            />
          </Stack>
          <FieldRow
            label={translate('Allow unlimited responses')}
            id="repeat-responses"
          >
            <ToggleSwitch
              id="repeat-responses"
              on={formData.allowRepeatResponses}
              onChange={(checked) =>
                setFormData({ ...formData, allowRepeatResponses: checked })
              }
            />
          </FieldRow>
          <FieldRow
            label={translate('Require student names')}
            id="require-names"
          >
            <ToggleSwitch
              id="require-names"
              disabled={isRostered}
              on={formData.requireNames}
              onChange={(checked) =>
                setFormData({ ...formData, requireNames: checked })
              }
            />
          </FieldRow>
          <div>
            <FieldRow
              label={
                <OneAttemptLabel canLimitOneAttempt={canLimitOneAttempt} />
              }
              id="one-attempt"
            >
              <ToggleSwitch
                id="one-attempt"
                data-testid="one-attempt-toggle"
                disabled={!isRostered && hasPaidPlan}
                on={formData.oneAttempt}
                onChange={(checked) => handleOneAttemptChange(checked)}
              />
            </FieldRow>
            {showRosterMessage ? (
              <div className="pb-4">
                <AlertMessage
                  tone="info"
                  showIcon
                  onClose={() => setShowRosterMessage(false)}
                  closeLabel={translate('Close')}
                >
                  {translate('Create a roster to use One Attempt.')}
                </AlertMessage>
              </div>
            ) : null}
          </div>
        </Stack>
        <Divider />
        <div className="pt-8">
          <Inline space="4" align="right">
            <UIButton
              appearance="primary"
              onClick={() => {
                setIsConfirmButtonDisabled(true);
                onConfirmClick(formData);
              }}
              isDisabled={isConfirmButtonDisabled}
            >
              {translate('Start')}
            </UIButton>
            <UIButton onClick={onClose}>{translate('Cancel')}</UIButton>
          </Inline>
        </div>
      </Dialog>
      <OneAttemptPaywall
        open={paywallOpen}
        onClose={() => dispatch({ type: 'CLOSE' })}
      />
    </>
  );
}
